<template>
  <el-card>
    <h3>Survey details</h3>
    <el-form
      :model="surveyForm"
      :rules="rules"
      label-position="left"
      label-width="300px"
      ref="surveyForm"
    >
      <el-form-item label="Title" prop="title">
        <el-input v-model="surveyForm.title"></el-input>
        <help-text>What would you like this survey to be called?</help-text>
      </el-form-item>

      <el-form-item label="Description" prop="description">
        <el-input v-model="surveyForm.description"></el-input>
        <help-text>A brief summary of what our survey is about.</help-text>
      </el-form-item>

      <el-form-item label="Date range" prop="dateRange">
        <el-date-picker
          v-model="surveyForm.dateRange"
          type="daterange"
          start-placeholder="Start date"
          end-placeholder="End date"
        />
        <help-text>Period of time in which we'll be running this survey.</help-text>
      </el-form-item>

      <el-form-item label="Survey Type" prop="surveyType">
        <el-radio-group v-model="surveyForm.surveyType">
          <el-radio-button label="QUALITATIVE">Qualitative Survey</el-radio-button>
          <el-radio-button label="MUSIC">Music Survey</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-divider></el-divider>
      <h3>Completion</h3>

      <el-form-item label="On survey completed">
        <el-radio-group v-model="surveyForm.successAutoRedirect">
          <el-radio-button :label="false">Display a message</el-radio-button>
          <el-radio-button :label="true">Redirect to a webpage</el-radio-button>
        </el-radio-group>
        <help-text>What should we do once a listener has completed this survey?</help-text>
      </el-form-item>
      <el-form-item
        label="Success message"
        v-if="!surveyForm.successAutoRedirect"
        prop="successText"
      >
        <el-input type="textarea" v-model="surveyForm.successText"></el-input>
        <help-text>Once the listener has completed their survey, we'll display this message.</help-text>
      </el-form-item>

      <el-form-item
        label="Redirect URL"
        v-if="surveyForm.successAutoRedirect"
        prop="successRedirect"
      >
        <el-input v-model="surveyForm.successRedirect" placeholder="https://"></el-input>
        <help-text>Once the listener has completed their survey, we'll take them to this webpage.</help-text>
      </el-form-item>

      <el-divider></el-divider>
      <h3>Privacy</h3>

      <el-form-item label="Privacy Policy URL" prop="privacyPolicy">
        <el-input v-model="surveyForm.privacyPolicy" placeholder="https://"></el-input>
        <help-text>Should we include a link to our privacy policy?</help-text>
      </el-form-item>

      <el-form-item label="Collect non voluntary identifiers" prop="collectNonVoluntaryIdentifiers">
        <el-switch
          v-model="surveyForm.collectNonVoluntaryIdentifiers"
          active-text="Yes"
          inactive-text="No"
        ></el-switch>
        <help-text>
          Should we capture information from the listener's browser, that could uniquely identify
          the listener, such as ip address and browser / device fingerprints? (Please make mention
          of this within your privacy policy should you choose this option)
        </help-text>
      </el-form-item>
      <el-form-item label="Show progress bar" prop="showProgressBar">
        <el-switch
          v-model="surveyForm.showProgressBar"
          active-text="Yes"
          inactive-text="No"
        ></el-switch>
        <help-text>
          Turning this on will show a progress Bar at the bottom of each survey page.
        </help-text>
      </el-form-item>

      <el-form-item>
        <el-button type="primary" :loading="surveySaving" @click="handleSaveSurvey">Save Survey</el-button>
        <el-button :loading="surveySaving" @click="handleCancel">Cancel</el-button>
      </el-form-item>
    </el-form>
  </el-card>
</template>

<script>
import { mapState } from 'vuex';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

import HelpText from '@/components/common/HelpText';

export default {
  name: 'SetupTab',
  components: {
    HelpText,
  },
  data: () => ({
    // FORM
    successMethod: 'text',
    surveyForm: {
      title: '',
      description: '',
      dateRange: [],
      startDate: null,
      endDate: null,
      successText: '',
      successRedirect: '',
      successAutoRedirect: false,
      privacyPolicy: '',
      collectNonVoluntaryIdentifiers: true,
      showProgressBar: false,
      logoUrl: '',
      surveyType: 'QUALITATIVE',
    },
    progressBarOnValue: 1,
    progressBarOfValue: 0,
    rules: {
      title: [
        { required: true, message: 'Please provide a title for this survey', trigger: 'blur' },
      ],
      dateRange: [
        {
          required: true,
          message: 'Please provide the start and end date for this survey',
          trigger: 'blur',
        },
      ],
    },
  }),
  computed: {
    ...mapState('user', ['me']),
    ...mapState('surveybuilder', ['survey', 'surveySaving']),
  },
  methods: {
    initFormData() {
      this.surveyForm = {
        title: this.survey.title,
        description: this.survey.description,
        dateRange: [this.survey.startDate, this.survey.endDate],
        startDate: this.survey.startDate,
        endDate: this.survey.endDate,
        successText: this.survey.successText,
        successRedirect: this.survey.successRedirect,
        successAutoRedirect: this.survey.successAutoRedirect,
        privacyPolicy: this.survey.privacyPolicy,
        collectNonVoluntaryIdentifiers: this.survey.collectNonVoluntaryIdentifiers,
        logoUrl: this.survey.logoUrl,
        surveyType: this.survey.surveyType,
        showProgressBar: this.survey.pageSize === this.progressBarOnValue,
      };
    },
    handleCancel() {
      this.$router.push({ path: '/surveyBuilder' });
    },
    handleSaveSurvey() {
      this.$refs.surveyForm.validate(valid => {
        if (valid) {
          const surveyData = {
            title: this.surveyForm.title,
            description: this.surveyForm.description,
            startDate: this.surveyForm.dateRange[0],
            endDate: this.surveyForm.dateRange[1],
            successText: this.surveyForm.successText,
            successRedirect: this.surveyForm.successRedirect,
            successAutoRedirect: this.surveyForm.successAutoRedirect,
            privacyPolicy: this.surveyForm.privacyPolicy,
            collectNonVoluntaryIdentifiers: this.surveyForm.collectNonVoluntaryIdentifiers,
            surveyType: this.surveyForm.surveyType,
            pageSize: this.get_survey_page(),
            isPreview: true,
            isActive: false,
            createdById: this.me.id,
          };

          // Determine if we should create or update
          if (this.survey && this.survey.id) {
            surveyData.isPreview = this.survey.isPreview;
            surveyData.isActive = this.survey.isActive;
            this.$store
              .dispatch('surveybuilder/surveyUpdate', {
                id: this.survey.id,
                ...surveyData,
              })
              .then(() => {
                this.$notify({
                  title: 'Survey Updated',
                  message: `${this.survey.title} updated successfully.`,
                  type: 'success',
                });
              });
          } else {
            this.$store.dispatch('surveybuilder/surveyCreate', surveyData).then(() => {
              this.$router.push({ path: `/surveybuilder/editor/${this.survey.id}` });

              this.$notify({
                title: 'Survey Created',
                message: `${this.survey.title} created successfully`,
                type: 'success',
              });
            });
          }

          this.$store.commit(`surveybuilder/${surveyBuilderTypes.SET_TAB_ACTIVE}`, 'canvas');
        }
      });
    },
    get_survey_page() {
      return this.surveyForm.showProgressBar ? this.progressBarOnValue : this.progressBarOfValue;
    },
  },
  watch: {
    survey() {
      this.initFormData();
    },
  },
};
</script>



// WEBPACK FOOTER //
// SetupTab.vue?e4ca5ef2